/**
 * When the background preload may re-LAY-OUT a database node, and when it must
 * leave the user's open subtree alone.
 *
 * `applySchemaGroupingToDatabaseNode` carries expansion state and lazily-loaded
 * children across a rebuild by matching old and new nodes on their ids — which
 * works only while the ids stay the same. A layout FLIP changes them: the flat
 * `programmability-folder<->conn<->db` becomes the schema-scoped
 * `programmability-folder<->conn<->db<->dbo`, so nothing matches, every rebuilt
 * node is fresh and collapsed, and whatever the user had open is detached.
 *
 * That is harmless on a database nobody has opened — the everyday case, and the
 * reason the flip is applied at all. It is not harmless underneath a user who
 * is three folders deep: with the home database now leading the preload wave
 * (rather than being preloaded before the listing), its objects land exactly
 * while someone is expanding it, and the subtree they are clicking through is
 * replaced under them.
 *
 * So: a flip needs either an empty/closed subtree, or an explicit request. A
 * user-initiated grouping-mode change and a Refresh both pass
 * `allowLayoutChange` and are unaffected.
 */

import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';

/** Folder types whose ids change shape between the flat and grouped layouts. */
const LAYOUT_SENSITIVE_TYPES = new Set<string>([
  'tables-folder',
  'views-folder',
  'programmability-folder',
  'schema-container-folder',
]);

/**
 * True when anything in this subtree is open.
 *
 * `isExpanded` is the signal: a freshly built database node is entirely
 * collapsed, so the only way it becomes true is a user (or a restore) opening
 * it. Loaded-but-collapsed children do not count — nothing is on screen to lose.
 */
export function hasExpandedDescendant(nodes: readonly DatabaseExplorerNode[] | undefined): boolean {
  if (!nodes?.length) return false;
  for (const node of nodes) {
    if (node.isExpanded) return true;
    if (hasExpandedDescendant(node.children as DatabaseExplorerNode[] | undefined)) return true;
  }
  return false;
}

/** Whether the database node is currently rendered in the schema-grouped shape. */
export function isCurrentlyGrouped(children: readonly DatabaseExplorerNode[]): boolean {
  return children.some((child) => child.type === 'schema-container-folder');
}

export interface LayoutChangeDecision {
  currentChildren: readonly DatabaseExplorerNode[];
  /** What the grouping rules say this database's layout should be. */
  willGroup: boolean;
  /** The caller asked for the layout explicitly (grouping-mode change, Refresh). */
  allowLayoutChange?: boolean;
}

/**
 * `true` when the restructure must be skipped entirely and the current children
 * kept as they are.
 */
export function shouldKeepCurrentLayout({
  currentChildren,
  willGroup,
  allowLayoutChange,
}: LayoutChangeDecision): boolean {
  if (allowLayoutChange) return false;
  // Same shape ⇒ the ids line up and `carryOverViewState` preserves identity.
  if (isCurrentlyGrouped(currentChildren) === willGroup) return false;
  // `hasExpandedDescendant` checks each node itself before recursing, so the
  // folders' own `isExpanded` is already covered.
  return hasExpandedDescendant(
    currentChildren.filter((child) => LAYOUT_SENSITIVE_TYPES.has(child.type)),
  );
}
